import { extractBackgroundIdFromUrl } from './hangoutUtils.js';

const CHAR_LIMIT = 3;

const mapValue = (obj, key, value) => {
  if (value !== undefined) obj[key] = value;
};

// Create a delta-based state diff that only includes changed values
const createStateDelta = (newState, previousState = {}) => {
  const delta = {};

  // Compare location
  if (newState.loc !== undefined && newState.loc !== previousState.loc) {
    delta.loc = newState.loc;
  }

  // Compare time of day
  if (newState.timeOfDay !== undefined && newState.timeOfDay !== previousState.timeOfDay) {
    delta.timeOfDay = newState.timeOfDay;
  }

  // Compare active characters (deep comparison)
  if (newState.activeCharacters && !areCharacterArraysEqual(newState.activeCharacters, previousState.activeCharacters)) {
    delta.activeCharacters = newState.activeCharacters;
  }

  // Include goal changes if present
  if (newState.goalChanges && Object.keys(newState.goalChanges).length > 0) {
    delta.goalChanges = newState.goalChanges;
  }

  return delta;
};

const areCharacterArraysEqual = (arr1, arr2) => {
  if (!arr1 && !arr2) return true;
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;

  return arr1.every((char1, index) => {
    const char2 = arr2[index];
    return char1.id === char2.id &&
      char1.outfitId === char2.outfitId &&
      char1.actionId === char2.actionId &&
      char1.goal === char2.goal;
  });
};

export function frontendToDbStateDiff(frontendStateDiff) {
  if (!frontendStateDiff || typeof frontendStateDiff !== 'object') return {};

  const db = {};

  mapValue(db, 'loc_param', frontendStateDiff.loc ?? frontendStateDiff.locationId);

  mapValue(db, 'time_of_day_param', frontendStateDiff.timeOfDay);

  if (Array.isArray(frontendStateDiff.activeCharacters)) {
    db.active_characters = frontendStateDiff.activeCharacters.slice(0, CHAR_LIMIT);
  }

  if (frontendStateDiff.goalChanges && typeof frontendStateDiff.goalChanges === 'object') {
    db.goal_changes = frontendStateDiff.goalChanges;
  }

  return db;
}

export function dbToFrontendStateDiff(dbStateDiff) {
  if (!dbStateDiff || typeof dbStateDiff !== 'object') return {};

  const frontend = {};

  // New RPC param-based mapping
  if (dbStateDiff.loc_param !== undefined) {
    frontend.locId = dbStateDiff.loc_param;
  }
  mapValue(frontend, 'timeOfDay', dbStateDiff.time_of_day_param);

  if (Array.isArray(dbStateDiff.active_characters)) {
    // JSON array
    frontend.activeCharacters = dbStateDiff.active_characters;
  }

  if (dbStateDiff.goal_changes !== undefined) frontend.goalChanges = dbStateDiff.goal_changes;

  // Legacy/raw base_chats state_diff mapping (location/current_time/characters)
  if (frontend.locId === undefined && dbStateDiff.location !== undefined) {
    frontend.locId = dbStateDiff.location;
  }
  if (frontend.timeOfDay === undefined && dbStateDiff.current_time !== undefined) {
    // Keep timeOfDay unchanged; frontend time progression will handle actual slot
  }
  if (!frontend.activeCharacters && Array.isArray(dbStateDiff.characters)) {
    frontend.activeCharacters = dbStateDiff.characters.map(c => {
      const ch = { id: String(c.id) };
      if (c.outfit_id !== undefined && c.outfit_id !== null) ch.outfitId = String(c.outfit_id);
      if (c.action_id !== undefined) ch.actionId = Number.parseInt(String(c.action_id), 10);
      return ch;
    });
  }

  return frontend;
}

export function createStateDiffFromMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return {};

  const diff = {};

  if (metadata.locationId !== undefined) {
    diff.locId = metadata.locationId;
  }
  mapValue(diff, 'timeOfDay', metadata.timeOfDay);

  if (Array.isArray(metadata.activeCharacters)) {
    diff.activeCharacters = metadata.activeCharacters.map(char => ({
      id: String(char.id),
      outfitId: String(char.outfitId || 0),
      actionId: char.actionId ?? null,
      name: char.name || `Character ${char.id}`,
      goal: char.goal || null
    }));
  }

  mapValue(diff, 'goalChanges', metadata.goalChanges);

  return diff;
}

export async function extractCurrentStateMetadata(hangoutState) {
  try {
    const metadata = {};

    try {
      const bg = await hangoutState.getSelectedBackground();
      if (bg) {
        metadata.backgroundUrl = bg;
        metadata.locationId = extractBackgroundIdFromUrl(bg);
      }
    } catch { }

    metadata.timeOfDay = hangoutState.getCurrentTimeSlot() || 'night';

    try {
      const chars = [];
      const session = hangoutState.getCurrentSession();

      if (session?.allCharacters?.length) {
        session.allCharacters.forEach(char => chars.push({
          id: String(char.id),
          outfitId: String(char.outfitId || 0),
          actionId: char.actionId ?? null,
          name: char.name || `Character ${char.id}`,
          goal: char.goal || null
        }));
      } else if (session?.characterId) {
        const char = hangoutState.getSelectedCharacter();
        if (char) {
          chars.push({
            id: String(char.id),
            outfitId: String(char.outfitId || session.outfitId || 0),
            actionId: char.actionId ?? null,
            name: char.name || `Character ${char.id}`,
            goal: hangoutState.getCurrentGoal(char.id) || null
          });
        }
      }

      metadata.activeCharacters = chars;
    } catch {
      metadata.activeCharacters = [];
    }

    return metadata;
  } catch {
    return {};
  }
}

// Create a delta state diff from metadata, comparing against previous state
export function createStateDeltaFromMetadata(metadata, previousState = {}) {
  const newState = createStateDiffFromMetadata(metadata);
  return createStateDelta(newState, previousState);
}

// Reconstruct full state from a chain of delta state diffs
export function reconstructStateFromDeltas(stateDiffs, initialState = {}, scenarioBaseline = null) {
  // Use scenario baseline as foundation if provided
  let currentState = { ...initialState };
  if (scenarioBaseline) {
    currentState = {
      loc: scenarioBaseline.loc || scenarioBaseline.loc_id || initialState.loc || 0,
      locationId: scenarioBaseline.loc || scenarioBaseline.loc_id || initialState.locationId || 0,
      timeOfDay: scenarioBaseline.timeOfDay || initialState.timeOfDay || 'night',
      activeCharacters: initialState.activeCharacters || [],
      goalChanges: initialState.goalChanges || {},
      ...initialState
    };
  }

  for (const stateDiff of stateDiffs) {
    if (!stateDiff) continue;

    // Apply each property from the state diff
    if (stateDiff.loc !== undefined) {
      currentState.locId = stateDiff.loc;
    }

    if (stateDiff.timeOfDay !== undefined) {
      currentState.timeOfDay = stateDiff.timeOfDay;
    }

    if (stateDiff.activeCharacters !== undefined) {
      currentState.activeCharacters = [...stateDiff.activeCharacters];
    }

    if (stateDiff.goalChanges !== undefined) {
      currentState.goalChanges = {
        ...currentState.goalChanges,
        ...stateDiff.goalChanges
      };
    }
  }

  return currentState;
}

export function validateStateDiff(stateDiff) {
  const result = { isValid: true, errors: [] };

  if (!stateDiff || typeof stateDiff !== 'object') {
    result.isValid = false;
    result.errors.push('State diff must be an object');
    return result;
  }

  if (stateDiff.activeCharacters !== undefined) {
    if (!Array.isArray(stateDiff.activeCharacters)) {
      result.isValid = false;
      result.errors.push('activeCharacters must be an array');
    } else if (stateDiff.activeCharacters.length > CHAR_LIMIT) {
      result.isValid = false;
      result.errors.push(`activeCharacters cannot exceed ${CHAR_LIMIT} characters`);
    } else {
      stateDiff.activeCharacters.forEach((char, i) => {
        if (!char.id) {
          result.isValid = false;
          result.errors.push(`Character ${i} missing id`);
        }
        if (char.outfitId && typeof char.outfitId !== 'string') {
          result.errors.push(`Character ${i} outfitId must be string`);
        }
      });
    }
  }

  if (stateDiff.locId !== undefined && stateDiff.locId !== null) {
    const type = typeof stateDiff.locId;
    if (type !== 'string' && type !== 'number') {
      result.isValid = false;
      result.errors.push('locId must be string or number');
    }
  }

  return result;
}

export { createStateDelta, areCharacterArraysEqual };
